/**
 * Local & Remote Runtime Client
 *
 * Provides utilities for connecting to a local StandardRuntimeHost or a remote
 * ACA runtime instead of going through Azure Resource Graph discovery.
 *
 * Also exports shared mock data (e.g. LOCAL_MOCK_APP) used by hooks and services
 * that need a fake app entry in local mode.
 *
 * Modes:
 * - Local mode (VITE_LOCAL_MODE=true): Routes to localhost, no auth needed
 * - Remote mode (VITE_REMOTE_RUNTIME_URL set): Routes to a remote ACA FQDN,
 *   useful for running the portal locally against a hosted ACA app
 * - Cloud mode (default): Resource Graph discovery, direct FQDN calls
 *
 * Architecture:
 * - Runtime APIs (runs, operations, A2A): Direct to StandardRuntimeHost, no auth needed
 * - Workflow files (definition, connections, parameters): Served by Vite plugin from local filesystem
 */

import type { AutomationAppUIModel } from '@/types/automation';

// Environment configuration
export const isLocalMode = (): boolean => {
  return import.meta.env.VITE_LOCAL_MODE === 'true';
};

/**
 * Remote mode: local portal pointing to a remote ACA runtime URL.
 * Set VITE_REMOTE_RUNTIME_URL to the ACA app's FQDN.
 *
 * TODO: Wire isRemoteMode/getRemoteRuntimeUrl into URL builders (buildRuntimeUrl,
 * buildVfsUrl in acaAppUrl.ts) so that setting VITE_REMOTE_RUNTIME_URL actually
 * reroutes calls. Currently these functions are unused.
 */
export const isRemoteMode = (): boolean => {
  return !isLocalMode() && !!import.meta.env.VITE_REMOTE_RUNTIME_URL;
};

export const getRemoteRuntimeUrl = (): string => {
  return import.meta.env.VITE_REMOTE_RUNTIME_URL || '';
};

export const getLocalRuntimeUrl = (): string => {
  // Support both full URL and port-only configuration
  if (import.meta.env.VITE_LOCAL_RUNTIME_URL) {
    return import.meta.env.VITE_LOCAL_RUNTIME_URL;
  }
  const port = import.meta.env.VITE_LOCAL_RUNTIME_PORT || '7071';
  return `http://localhost:${port}`;
};

// URL for local Vite plugin endpoints (/local/*)
// In dev mode, these are served by the Vite plugin on the same origin
export const getLocalPluginUrl = (): string => {
  // Same origin - Vite plugin serves /local/* routes
  return '';
};

/**
 * Transforms an ARM hostruntime URL to a local runtime URL.
 *
 * ARM pattern: https://management.azure.com/.../sites/{app}/hostruntime/runtime/webhooks/workflow/api/management/...
 * Local pattern: http://localhost:7071/runtime/webhooks/workflow/api/management/...
 *
 * The path after /hostruntime in ARM is identical to the local runtime path.
 */
export const transformToLocalUrl = (armUrl: string): string => {
  const localBase = getLocalRuntimeUrl();

  // Extract the runtime path from ARM URL
  // Pattern: .../hostruntime/runtime/webhooks/workflow/api/management/...
  const hostruntimeMatch = armUrl.match(/\/hostruntime(\/runtime\/webhooks\/workflow\/api\/management\/.+)$/);
  if (hostruntimeMatch) {
    return `${localBase}${hostruntimeMatch[1]}`;
  }

  // For VFS paths (parameters.json, etc.)
  // Pattern: .../hostruntime/admin/vfs/...
  const vfsMatch = armUrl.match(/\/hostruntime(\/admin\/vfs\/.+)$/);
  if (vfsMatch) {
    return `${localBase}${vfsMatch[1]}`;
  }

  // If no pattern matches, return original (shouldn't happen for runtime APIs)
  console.warn('Could not transform URL to local:', armUrl);
  return armUrl;
};

export interface LocalFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * Fetch wrapper for local runtime API calls.
 * Does NOT include Authorization header - local runtime doesn't need it.
 * Includes fake caller identity headers for A2A endpoints (matching MCP server pattern).
 */
export const localFetch = async <T>(url: string, options: LocalFetchOptions = {}): Promise<T> => {
  const { method = 'GET', body, headers = {}, signal } = options;

  const fetchHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    // Fake caller identity for local development - must match MCP server values
    // to allow portal to interact with runs created by MCP tools
    'x-ms-client-object-id': 'local-test-user',
    'x-ms-client-tenant-id': 'local-test-tenant',
    ...headers,
  };

  const fetchOptions: RequestInit = {
    method,
    headers: fetchHeaders,
    signal: signal ?? null,
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Local runtime API request failed: ${response.status} ${errorText}`);
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text);
};

/**
 * Fetch with conditional routing - uses local runtime in local mode,
 * otherwise uses the provided ARM URL with auth token.
 */
export const fetchWithLocalSupport = async <T>(
  armUrl: string,
  accessToken: string,
  options: LocalFetchOptions = {}
): Promise<T> => {
  if (isLocalMode()) {
    const localUrl = transformToLocalUrl(armUrl);
    return localFetch<T>(localUrl, options);
  }

  // Standard ARM fetch with auth
  const { method = 'GET', body, headers = {} } = options;

  const fetchHeaders: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
    ...headers,
  };

  const fetchOptions: RequestInit = {
    method,
    headers: fetchHeaders,
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(armUrl, fetchOptions);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API request failed: ${response.status} ${errorText}`);
  }

  return response.json();
};

/**
 * Get mock app context for local development.
 * Returns a fake app entry that points to the local runtime.
 */
export const getLocalAppContext = () => ({
  subscriptionId: 'local-subscription',
  resourceGroup: 'local-resource-group',
  appName: 'local-runtime',
  location: 'localhost',
  defaultHostName: getLocalRuntimeUrl().replace(/^https?:\/\//, ''),
});

const LOCAL_ARM_RESOURCE_ID = '/subscriptions/local-subscription/resourceGroups/local-resource-group/providers/Microsoft.App/containerApps/local-runtime';

/**
 * Generate mock apps for a given project in local mode.
 * Used as a fallback when the mock control plane is unreachable,
 * and by global app queries (resource graph) that don't go through the control plane.
 */
export const getLocalMockApps = (projectName: string): AutomationAppUIModel[] => {
  const appNames = ['local-runtime', 'order-processing', 'data-sync'];
  return appNames.map((name) => ({
    id: `/subscriptions/local-subscription/resourceGroups/${projectName}/providers/Microsoft.Logic/automationProjects/${projectName}/applications/${name}`,
    name,
    type: 'applications' as const,
    projectName,
    location: 'local',
    armResourceId: `/subscriptions/local-subscription/resourceGroups/${projectName}/providers/Microsoft.App/containerApps/${name}`,
    managedBy: null,
    etag: `"local-${name}-etag"`,
    tags: { env: 'local', owner: 'local-dev' },
    identity: {
      type: 'SystemAssigned',
      principalId: '00000000-0000-0000-0000-000000000001',
      tenantId: '00000000-0000-0000-0000-000000000002',
    },
    systemData: {
      createdBy: 'local-dev@example.com',
      createdByType: 'User',
      createdAt: '2026-03-15T00:00:00.000Z',
      lastModifiedBy: 'local-dev@example.com',
      lastModifiedByType: 'User',
      lastModifiedAt: '2026-04-10T00:00:00.000Z',
    },
    properties: {
      applicationId: name,
      appResourceId: `/subscriptions/local-subscription/resourceGroups/${projectName}/providers/Microsoft.App/containerApps/${name}`,
      createdBy: 'local-dev',
      createdOn: '2026-03-15T00:00:00.000Z',
      updatedBy: 'local-dev',
      updatedOn: '2026-04-10T00:00:00.000Z',
      fqdn: `${name}.local.azurecontainerapps.io`,
    },
  }));
};

/**
 * Mock AutomationAppUIModel for local mode.
 * Used by hooks and services that need a fake app entry when running against a local runtime.
 */
export const LOCAL_MOCK_APP: AutomationAppUIModel = {
  id: LOCAL_ARM_RESOURCE_ID,
  name: 'local-runtime',
  type: 'applications',
  projectName: 'local-resource-group',
  location: 'local',
  armResourceId: LOCAL_ARM_RESOURCE_ID,
  managedBy: null,
  etag: '"local-mock-etag"',
  tags: {},
  identity: { type: 'None' },
  systemData: {},
  properties: {
    applicationId: 'local-runtime',
    appResourceId: LOCAL_ARM_RESOURCE_ID,
    createdBy: '',
    createdOn: '',
    updatedBy: '',
    updatedOn: '',
    fqdn: null,
  },
};

/**
 * Check if the local runtime is available by pinging the health endpoint.
 */
export const checkLocalRuntimeHealth = async (): Promise<boolean> => {
  try {
    const url = `${getLocalRuntimeUrl()}/runtime/webhooks/workflow/api/management/workflows?api-version=2018-11-01`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return response.ok;
  } catch {
    return false;
  }
};
